/*
  Option C V1 — SBC Commercial minimal admin surface.

  Purpose (scope-limited):
    * resolve or create the SBC provider commercial record
    * submit the Gateway decision request and display the result
    * display existing Commercial Authorisation status for the provider
    * link an existing Owner-created Quote to the commercial context

  This surface deliberately does NOT expose providerCommercialRef or
  verticalCaseRef as editable Quote Builder fields.
*/
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { commercialApi } from "@/lib/adminApi";

const OFFER_IDS = [
  "OFFER_EXIT",
  "OFFER_AUDIT_READINESS",
  "OFFER_PATHWAY_CLARIFICATION",
  "OFFER_REGISTRATION_PATHWAY",
  "OFFER_SCOPE_REVIEW",
  "OFFER_MANUAL_REVIEW",
  "OFFER_NONE"
];

export function CommercialPanel() {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [authorisations, setAuthorisations] = useState([]);
  const [newProvider, setNewProvider] = useState({ abn: "", displayName: "" });
  const [gateway, setGateway] = useState({
    verticalCaseRef: "",
    pathwayId: "",
    recommendedOfferId: "OFFER_EXIT",
    manualReviewRequired: false
  });
  const [gatewayResult, setGatewayResult] = useState(null);
  const [linkQuoteId, setLinkQuoteId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadProviders = useCallback(async () => {
    try {
      const list = await commercialApi.listProviders();
      setProviders(list);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  const loadAuthorisations = useCallback(async (providerRef) => {
    if (!providerRef) return setAuthorisations([]);
    try {
      const list = await commercialApi.listAuthorisations({ providerCommercialRef: providerRef });
      setAuthorisations(list);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);
  useEffect(() => {
    if (selectedProvider) loadAuthorisations(selectedProvider.id);
  }, [selectedProvider, loadAuthorisations]);

  const handleCreateProvider = async () => {
    setError(""); setStatus("");
    try {
      const record = await commercialApi.createProvider(newProvider);
      setStatus(`Created providerCommercialRef ${record.id}.`);
      setNewProvider({ abn: "", displayName: "" });
      await loadProviders();
      setSelectedProvider(record);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const handleGateway = async () => {
    setError(""); setStatus(""); setGatewayResult(null);
    if (!selectedProvider) { setError("Select a provider first."); return; }
    try {
      const res = await commercialApi.decideGateway({
        providerCommercialRef: selectedProvider.id,
        verticalCaseRef: gateway.verticalCaseRef,
        pathwayId: gateway.pathwayId,
        recommendedOfferId: gateway.recommendedOfferId,
        manualReviewRequired: gateway.manualReviewRequired
      });
      setGatewayResult(res);
      setStatus(`Gateway decision: ${res.gatewayDecision}`);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const handleLinkQuote = async () => {
    setError(""); setStatus("");
    if (!selectedProvider) { setError("Select a provider first."); return; }
    if (!gateway.verticalCaseRef) { setError("Enter a verticalCaseRef first."); return; }
    if (!linkQuoteId) { setError("Enter a Quote id to link."); return; }
    try {
      const res = await commercialApi.linkQuote(linkQuoteId, {
        providerCommercialRef: selectedProvider.id,
        verticalCaseRef: gateway.verticalCaseRef
      });
      setStatus(`Linked Quote ${res.id} to providerCommercialRef ${res.providerCommercialRef}.`);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  return (
    <div className="space-y-8" data-testid="commercial-panel">
      <div className="rounded-sm border border-amber-300/50 bg-amber-50/40 p-3 text-xs leading-relaxed text-amber-900">
        SBC Commercial (V1). Owner/Admin only. Provider commercial identity
        (providerCommercialRef) is opaque — ABN is captured as an attribute,
        not identity. The Gateway does not recompute assessment logic and
        will not automatically create a Quote or return DECLINED.
      </div>

      {/* Provider section */}
      <section className="space-y-3">
        <h3 className="font-display text-lg font-medium">SBC Provider Commercial Record</h3>
        <div className="grid gap-2 md:grid-cols-2">
          <div>
            <Label>Select existing</Label>
            <select
              data-testid="commercial-provider-select"
              className="mt-1 w-full rounded-sm border p-2 text-sm"
              value={selectedProvider?.id || ""}
              onChange={(e) => {
                const p = providers.find((x) => x.id === e.target.value) || null;
                setSelectedProvider(p);
              }}
            >
              <option value="">— select —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>{p.displayName} · {p.abn} · {p.id.slice(0, 8)}</option>
              ))}
            </select>
          </div>
          <div className="rounded-sm border p-3">
            <p className="text-xs text-muted-foreground">Or create a new record (synthetic data only during V1 testing).</p>
            <div className="mt-2 grid gap-2">
              <Input
                data-testid="commercial-new-provider-abn"
                placeholder="ABN"
                value={newProvider.abn}
                onChange={(e) => setNewProvider((v) => ({ ...v, abn: e.target.value }))}
              />
              <Input
                data-testid="commercial-new-provider-name"
                placeholder="Display name"
                value={newProvider.displayName}
                onChange={(e) => setNewProvider((v) => ({ ...v, displayName: e.target.value }))}
              />
              <Button
                data-testid="commercial-create-provider-btn"
                onClick={handleCreateProvider}
                disabled={!newProvider.abn || !newProvider.displayName}
                className="rounded-sm"
              >
                Create provider commercial record
              </Button>
            </div>
          </div>
        </div>
        {selectedProvider && (
          <div className="rounded-sm border bg-muted/40 p-3 text-xs" data-testid="commercial-selected-provider">
            <div><strong>providerCommercialRef:</strong> {selectedProvider.id}</div>
            <div><strong>ABN (attribute):</strong> {selectedProvider.abn}</div>
            <div><strong>Display name:</strong> {selectedProvider.displayName}</div>
          </div>
        )}
      </section>

      {/* Gateway section */}
      <section className="space-y-3">
        <h3 className="font-display text-lg font-medium">Commercial / Scope Gateway</h3>
        <div className="grid gap-2 md:grid-cols-2">
          <Input
            data-testid="commercial-gateway-verticalCaseRef"
            placeholder="verticalCaseRef (assessmentId)"
            value={gateway.verticalCaseRef}
            onChange={(e) => setGateway((g) => ({ ...g, verticalCaseRef: e.target.value }))}
          />
          <Input
            data-testid="commercial-gateway-pathwayId"
            placeholder="pathwayId (classifier.pathway.id)"
            value={gateway.pathwayId}
            onChange={(e) => setGateway((g) => ({ ...g, pathwayId: e.target.value }))}
          />
          <select
            data-testid="commercial-gateway-offer"
            className="rounded-sm border p-2 text-sm"
            value={gateway.recommendedOfferId}
            onChange={(e) => setGateway((g) => ({ ...g, recommendedOfferId: e.target.value }))}
          >
            {OFFER_IDS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="commercial-gateway-manualReview"
              checked={gateway.manualReviewRequired}
              onChange={(e) => setGateway((g) => ({ ...g, manualReviewRequired: e.target.checked }))}
            />
            manualReviewRequired (context only)
          </label>
        </div>
        <Button
          data-testid="commercial-gateway-submit-btn"
          onClick={handleGateway}
          disabled={!selectedProvider}
          className="rounded-sm"
        >
          Submit Gateway decision request
        </Button>
        {gatewayResult && (
          <div className="rounded-sm border bg-muted/40 p-3 text-xs" data-testid="commercial-gateway-result">
            <div><strong>Decision:</strong> {gatewayResult.gatewayDecision}</div>
            {gatewayResult.authorisedServiceCode && (
              <div><strong>authorisedServiceCode:</strong> {gatewayResult.authorisedServiceCode}</div>
            )}
            {gatewayResult.commercialAuthorisationRef && (
              <div><strong>commercialAuthorisationRef:</strong> {gatewayResult.commercialAuthorisationRef}</div>
            )}
            {gatewayResult.quoteRef && (
              <div><strong>quoteRef:</strong> {gatewayResult.quoteRef}</div>
            )}
            <div><strong>decisionTimestamp:</strong> {gatewayResult.decisionTimestamp}</div>
          </div>
        )}
      </section>

      {/* Authorisations for the selected provider */}
      <section className="space-y-3">
        <h3 className="font-display text-lg font-medium">Commercial Authorisations for this provider</h3>
        {authorisations.length === 0 ? (
          <p className="text-xs text-muted-foreground">No Commercial Authorisations recorded for this provider.</p>
        ) : (
          <ul className="space-y-2" data-testid="commercial-authorisations-list">
            {authorisations.map((a) => (
              <li key={a.id} className="rounded-sm border p-3 text-xs">
                <div><strong>id:</strong> {a.id}</div>
                <div><strong>status:</strong> {a.status}</div>
                <div><strong>authorisedServiceCode:</strong> {a.authorisedServiceCode}</div>
                <div><strong>verticalCaseRef:</strong> {a.verticalCaseRef}</div>
                <div><strong>quoteRef:</strong> {a.quoteRef || "—"}</div>
                <div><strong>supersedesRef:</strong> {a.supersedesRef || "—"}</div>
                <div><strong>authorisedAt:</strong> {a.authorisedAt}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Link Quote */}
      <section className="space-y-3">
        <h3 className="font-display text-lg font-medium">Link existing Quote to commercial context</h3>
        <p className="text-xs text-muted-foreground">
          The Quote must already exist (create it via the Quote builder first). This action attaches
          providerCommercialRef and verticalCaseRef to that Quote. It does not modify pricing or scope.
        </p>
        <div className="flex gap-2">
          <Input
            data-testid="commercial-link-quote-id"
            placeholder="Quote id"
            value={linkQuoteId}
            onChange={(e) => setLinkQuoteId(e.target.value)}
          />
          <Button
            data-testid="commercial-link-quote-btn"
            onClick={handleLinkQuote}
            disabled={!selectedProvider || !gateway.verticalCaseRef || !linkQuoteId}
            className="rounded-sm"
          >
            Link Quote
          </Button>
        </div>
      </section>

      {status && (
        <p className="rounded-sm border border-green-300/60 bg-green-50/50 p-2 text-xs text-green-900" data-testid="commercial-status">{status}</p>
      )}
      {error && (
        <p className="rounded-sm border border-red-300/60 bg-red-50/50 p-2 text-xs text-red-900" data-testid="commercial-error">{error}</p>
      )}
    </div>
  );
}

export default CommercialPanel;
